import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { FileService } from '@shared/services/files';
import { DprCard } from '@dpr/models';
import { filter, Observable, take, tap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ReportHistoryModalComponent } from '../report-history-modal/report-history-modal.component';
import { StaticMessage } from '@shared/enums';
import { MatDialog } from '@angular/material/dialog';
import { Constants } from '@shared/utils';
import { DprCardService } from '@dpr/services';
import { DprModalTitlePipe } from '@shared/pipes';
import { DprReportService, DownloadDprService } from '@report/services';

@UntilDestroy()
@Component({
  selector: 'app-dpr-card-details',
  templateUrl: './dpr-card-details.component.html',
  styleUrls: ['./dpr-card-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprCardDetailsComponent {
  @Input({ required: true }) data$: Observable<DprCard>;
  @Input({ required: true }) isDigitalSignatureEnabled: boolean;
  @Input({ required: true }) canEdit: boolean;
  @Input({ required: true }) isAppOnline: boolean;
  @Output() uploadSignedReport = new EventEmitter<File[]>();

  StaticMessage = StaticMessage;

  constructor(
    private fileService: FileService,
    private dialog: MatDialog,
    private dprReportService: DprReportService,
    private dprCardService: DprCardService,
    private downloadDprService: DownloadDprService,
    private dprModalTitlePipe: DprModalTitlePipe
  ) {}

  downloadDprReport(): void {
    this.data$
      .pipe(
        untilDestroyed(this),
        take(1),
        tap((cardData) => {
          if (this.isAppOnline) {
            this.openReportHistoryModal(cardData);
          } else {
            this.downloadDprService.downloadOffline(
              cardData.dprDetailId,
              !this.isDprComplete(cardData)
            );
          }
        })
      )
      .subscribe();
  }

  downloadSignedReport(): void {
    this.data$
      .pipe(
        untilDestroyed(this),
        take(1),
        filter((cardData) => !!cardData.isSignDprDetailsUploaded),
        tap((cardData) => {
          this.fileService.download(
            cardData.signedReportExternalUrl,
            cardData.signedReportName,
            cardData.signedReportFileType
          );
        })
      )
      .subscribe();
  }

  onUploadSignedReport(files: File[]): void {
    this.uploadSignedReport.emit(files);
  }

  private openReportHistoryModal(cardData: DprCard): void {
    this.dprReportService
      .getDprReportHistory(cardData.id)
      .pipe(
        untilDestroyed(this),
        tap((reportHistoryResponse) => {
          this.dialog.open(ReportHistoryModalComponent, {
            ...Constants.DialogOptions,
            disableClose: false,
            data: {
              addWatermark: !this.isDprComplete(cardData),
              dprDetailId: cardData.dprDetailId,
              displayId: cardData.displayId,
              hasImagesToPrint: cardData.hasImagesToPrint,
              isDigitalSignatureEnabled: this.isDigitalSignatureEnabled,
              title: this.dprModalTitlePipe.transform(
                this.dprCardService.getModalTitle(
                  cardData.displayId,
                  cardData.dateTime,
                  cardData.dprDetailRevision
                ),
                $localize`:@@dpr.dprCardDetails.txtDownload:Download`
              ),
              reportHistory: reportHistoryResponse,
            },
          });
        })
      )
      .subscribe();
  }

  private isDprComplete(data: DprCard): boolean {
    return data.dprDetailsFirstWarning == null;
  }
}
